import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { deleteDevice, getDevice, rotatePairingKey } from "../api/devices";
import { getGraceWindow } from "../api/provisioning";
import { consoleFeatures } from "../config/consoleFeatures";

export default function DeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useOutletContext();
  const [device, setDevice] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [grace, setGrace] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = profile?.role === "admin";

  useEffect(() => {
    let active = true;
    let pending = false;
    const refresh = async () => {
      if (pending || document.visibilityState === "hidden") return;
      pending = true;
      try {
        const row = await getDevice(null, id);
        if (active) { setDevice(row); setError(""); }
      } catch (err) { if (active) setError(err.message); }
      finally { pending = false; }
    };
    void refresh();
    const timer = setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    return () => { active = false; clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [id]);

  const checkGrace = async () => {
    setError("");
    try { setGrace(await getGraceWindow(device.beid)); } catch (err) { setError(err.message); }
  };

  const rotateKey = async () => {
    if (!window.confirm(`Rotate the pairing key for ${device.beid}? Existing app credentials will be revoked.`)) return;
    setBusy(true); setError(""); setNotice("");
    try {
      setDevice(await rotatePairingKey(device.id));
      setGrace(null);
      setNotice("Pairing key rotated. The edge must retrieve the new key before pairing.");
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${device.beid}? The edge may register it again later.`)) return;
    setBusy(true); setError("");
    try { await deleteDevice(device.id); navigate("/devices"); }
    catch (err) { setError(err.message); setBusy(false); }
  };

  if (error && !device) return <div className="alert alert-error">{error}</div>;
  if (!device) return <div className="spinner" />;

  return <div>
    <h2 className="page-title">
      {consoleFeatures.showDeviceName && device.name ? (
        <>{device.name} <span className="mono muted" style={{ fontSize: 15 }}>({device.beid})</span></>
      ) : (
        <span className="mono">{device.beid}</span>
      )}
    </h2>
    <p className="page-sub">Edge inventory and provisioning details</p>
    {error && <div className="alert alert-error">{error}</div>}
    {notice && <div className="alert alert-success">{notice}</div>}
    <div className="card"><h2>Actions</h2><div className="row">
      <Link to={`/DeviceCTOValidation/${device.id}`} className="btn btn-outline btn-sm">Validate Root CA</Link>
      <button className="btn btn-outline btn-sm" onClick={checkGrace}>Check grace window</button>
      {isAdmin && <><button className="btn btn-outline btn-sm" onClick={rotateKey} disabled={busy || !device.is_registered}>Rotate pairing key</button><button className="btn btn-outline btn-sm" onClick={remove} disabled={busy}>Delete device</button></>}
    </div>
    {grace && <p style={{ marginTop: 12 }}>Grace window: <span className={`badge ${grace.active ? "badge-green" : "badge-red"}`}>{grace.active ? "active" : "inactive"}</span>{" "}<span className="muted">{grace.expires ? `expires ${String(grace.expires)}` : "no expiry"}</span></p>}
    </div>
    <div className="card"><h2>Overview</h2><div className="table-wrap"><table className="tbl"><tbody>
      <tr><td className="muted">BEID</td><td className="mono">{device.beid}</td></tr>
      {consoleFeatures.showDeviceName && <tr><td className="muted">Name</td><td>{device.name || "—"}</td></tr>}
      <tr><td className="muted">Registration</td><td>{device.is_registered ? "Registered" : "Unregistered"}</td></tr>
      <tr><td className="muted">State</td><td>{device.enrollment_state}</td></tr>
      <tr><td className="muted">Installers</td><td>{device.assigned_users?.length
        ? device.assigned_users.map((user) => user.full_name || user.email).join(", ")
        : (device.assigned_user_email || "Unassigned")}</td></tr>
      {consoleFeatures.showSharedKeyVersion && <tr><td className="muted">Shared key version</td><td>{device.shared_key_version}</td></tr>}
    </tbody></table></div></div>
    <div className="card"><h2>CTO Bundle</h2>{device.cto_bundle_json ? <pre className="json">{JSON.stringify(device.cto_bundle_json, null, 2)}</pre> : <div className="empty">No CTO bundle attached.</div>}</div>
  </div>;
}
